import { bool, func, shape, string } from 'prop-types';
import Card from '../../shared/Card';
import Header from '../../shared/Header';
import Input from '../../shared/Input';
import Stack from '../../shared/layouts/Stack';
import Select from '../../shared/Select';
import { useQuery } from 'react-query';
import axios from 'axios';
import CheckBox from '../../shared/CheckBox';
import { FormStack } from '../../shared/layouts';
import LonelyPlaceholder from '../../shared/LonelyPlaceholder';
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";

const AddressSection = ({
  handleChange,
  isContactAddress,
  same_as_house,
  zipcode,
  housenumber,
  housenumber_addition,
  street,
  place,
  errors,
  country,
  province,
  readOnly,
  title,
}) => {
  const { t } = useTranslation();
  if (country) country = country.toUpperCase();
  if (province) province = province.toUpperCase();

  if (title === null) {
    title = t('Adresgegevens');
  }

  const { data: apiData = {} } = useQuery(
    'countries',
    async () => await axios('/dossier/countries'),
    { staleTime: Infinity, refetchOnMount: false }
  );

  const countryOptions =
    apiData?.data &&
    Object.keys(apiData?.data).map((key) => ({
      value: apiData?.data[key]?.iso_3166_1_alpha2,
      label: apiData?.data[key]?.native_name,
    }));

  const provinceOptions =
    country &&
    apiData?.data &&
    apiData?.data[country.toLowerCase()] &&
    Object.keys(apiData.data[country.toLowerCase()]?.divisions).map((key) => ({
      value: key,
      label: apiData.data[country.toLowerCase()].divisions[key].name,
    }));

  const { HouseIcon } = useIconLibrary();

  return (
    <Card>
      <Stack space="m" width="100%">
        <Header
          title={title}
          icon={<HouseIcon height="2rem" width="2rem" />}
          variant="section"
        />
        {(isContactAddress && !readOnly) && (
          <CheckBox
            name="same_as_house"
            title={t("Adres komt overeen met woning")}
            value={same_as_house}
            onChange={handleChange}
            errors={errors?.same_as_house}
            readOnly={readOnly}
          />
        )}
        <FormStack
          space="m"
          readOnly={readOnly}
          hSelector={'&:nth-child(n+4)'}
          vSelector={'&:nth-child(2), &:nth-child(2n+3)'}
          customChildSelector={{
            '&:nth-child(-n+3)': { flex: '20% 1 0 !important' },
          }}
        >
          {/* Row */}
          <Input
            name="street"
            label={t("Straatnaam")}
            value={street}
            onChange={handleChange}
            disabled={same_as_house}
            errors={errors.street}
            readOnly={readOnly}
          />
          <Input
            name="housenumber"
            label={t("Huisnummer")}
            value={housenumber}
            onChange={handleChange}
            inputMode="numeric"
            disabled={same_as_house}
            errors={errors.housenumber}
            readOnly={readOnly}
          />
          <Input
            name="housenumber_addition"
            label={t("Toevoeging")}
            value={housenumber_addition}
            onChange={handleChange}
            disabled={same_as_house}
            errors={errors.housenumber_addition}
            readOnly={readOnly}
          />
          {/* Row */}
          <Input
            name="zipcode"
            label={t("Postcode")}
            value={zipcode}
            onChange={handleChange}
            disabled={same_as_house}
            errors={errors.zipcode}
            readOnly={readOnly}
          />
          <Input
            name="place"
            label={t("Plaatsnaam")}
            value={place}
            onChange={handleChange}
            disabled={same_as_house}
            errors={errors.place}
            readOnly={readOnly}
          />
          {/* Row */}
          <Select
            name="country"
            label={t("Land")}
            onChange={handleChange}
            onFocus={(e) => {
              // Prevent Chromium's autocomplete-dropdown from overlapping the actual select-dropdown
              if (window.chrome) e.target.autocomplete = 'nope';
            }}
            options={countryOptions || []}
            value={country}
            disabled={same_as_house}
            isSearchable
            readOnly={readOnly}
          />
          <Select
            clearKey={country}
            label={t("Provincie")}
            name="province"
            onChange={handleChange}
            options={provinceOptions || []}
            value={province}
            disabled={
              same_as_house || !provinceOptions || provinceOptions.length === 0
            }
            isSearchable
            readOnly={readOnly}
          />
          <LonelyPlaceholder />
        </FormStack>
      </Stack>
    </Card>
  );
};

AddressSection.propTypes = {
  handleChange: func.isRequired,
  same_as_house: bool,
  readOnly: bool,
  isContactAddress: bool,
  zipcode: string,
  housenumber: string,
  housenumber_addition: string,
  street: string,
  place: string,
  errors: shape({}),
  country: string,
  province: string,
  title: string,
};

AddressSection.defaultProps = {
  same_as_house: false,
  readOnly: false,
  isContactAddress: false,
  zipcode: '',
  housenumber: '',
  housenumber_addition: '',
  street: '',
  place: '',
  country: '',
  province: '',
  errors: {},
  title: null,
};

export default AddressSection;
